import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso900114001DebouncedValue, useIso900114001LatestRequest } from "../../../components/shared/useIso900114001FilteredRequest";
import { getCalidadIncidencias } from "../api/calidad";
import type { CalidadControlModificacion } from "../../../types/control-modificaciones";
import type {
  CalidadIncidencia,
  CalidadIncidenciasFilters,
  CalidadIncidenciasMeta,
} from "../types/calidad";

const emptyMeta: CalidadIncidenciasMeta = {
  total: 0,
  next_numero: 1,
  current_page: 1,
  last_page: 1,
  per_page: 50,
  from: null,
  to: null,
};

export function useCalidadIncidencias(
  filters: CalidadIncidenciasFilters,
  page = 1,
  enabled = true,
) {
  const [incidencias, setIncidencias] = useState<CalidadIncidencia[]>([]);
  const [controlModificaciones, setControlModificaciones] = useState<CalidadControlModificacion[]>([]);
  const [meta, setMeta] = useState<CalidadIncidenciasMeta>(emptyMeta);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const { runLatest, resetRequestKey, hasActiveRequest } = useIso900114001LatestRequest();

  const request = useMemo(() => ({
    numero: filters.numero.trim() || undefined,
    search: debouncedSearch || undefined,
    tipo_origen: filters.tipo_origen || undefined,
    tipo: filters.tipo || undefined,
    page,
    per_page: 50,
  }), [debouncedSearch, filters.numero, filters.tipo, filters.tipo_origen, page]);

  const requestKey = useMemo(() => JSON.stringify(request), [request]);

  const reload = useCallback(async (force = true) => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => getCalidadIncidencias(request, signal), force);
      if (!response) return;
      setIncidencias(response.data);
      setControlModificaciones(response.control_modificaciones);
      setMeta(response.meta);
    } catch {
      setError("No se pudieron cargar las incidencias de calidad.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [enabled, hasActiveRequest, request, requestKey, runLatest]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void reload(false);
  }, [enabled, reload]);

  return { incidencias, controlModificaciones, meta, loading, error, reload, resetRequestKey };
}
